import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { User, AlertTriangle, CheckCircle, XCircle, ExternalLink, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import Modal from './Modal';
import { salesCyclesApi, insightsApi } from '../services/api';
import { handleCompanyNameClick } from '../utils/hubspot';

interface DealsListModalProps {
  isOpen: boolean;
  onClose: () => void;
  stageName: string;
  icon: string;
  dealsCount: number;
  // Modal type determines which API endpoint to call and how to display data
  modalType: 'attention' | 'category' | 'strategic-insights' | 'tactical-insights';
  // Only required for category type
  category?: string;
  categoryDisplayName?: string;
  // Only required for strategic-insights type
  reason?: string;
  // Only required for tactical-insights type
  criteria?: string;
  qualificationType?: 'INVESTIGATE' | 'FORGET' | 'ENTERPRISE' | 'TRANSACTIONAL';
  actionType?: 'OPEN_DEAL' | 'INVESTIGATE';
  // Navigation for unqualified deals
  onBackToSelection?: () => void;
  // Filter parameters
  selectedCycleTypes?: string[];
  selectedAccountValues?: string[];
  selectedSalesRep?: string | null;
  selectedTeam?: string | null;
}

const DealsListModal: React.FC<DealsListModalProps> = ({ 
  isOpen, 
  onClose, 
  stageName, 
  icon, 
  dealsCount,
  modalType,
  category,
  categoryDisplayName,
  reason,
  criteria,
  qualificationType,
  actionType,
  onBackToSelection,
  selectedCycleTypes,
  selectedAccountValues,
  selectedSalesRep,
  selectedTeam
}) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20); // Start with 20 items per page
  
  // Reset pagination when modal opens
  React.useEffect(() => {
    console.log('DealsListModal useEffect - isOpen:', isOpen, 'modalType:', modalType, 'criteria:', criteria);
    if (isOpen) {
      setCurrentPage(1);
    }
  }, [isOpen, modalType, criteria]);

  // Convert cycle types array to comma-separated string for API calls
  const cycleTypesString = selectedCycleTypes && selectedCycleTypes.length > 0 ? selectedCycleTypes.join(',') : undefined;

  // Use unified API endpoint for all deal filtering with pagination
  const { data: deals, isLoading, error } = useQuery<any>(
    ['deals-unified-v3', stageName, modalType, category, reason, criteria, currentPage, pageSize, cycleTypesString || 'all', selectedAccountValues?.join(',') || 'all', selectedSalesRep || 'all', selectedTeam || 'all', qualificationType || 'all', actionType || 'all'],
    () => {
      // Special handling for strategic insights
      if (modalType === 'strategic-insights') {
        return insightsApi.getClosedLostDealsByBucket(
          reason!,
          currentPage,
          pageSize,
          cycleTypesString,
          selectedAccountValues && selectedAccountValues.length > 0 ? selectedAccountValues.join(',') : undefined,
          selectedSalesRep,
          selectedTeam
        );
      }

      // Special handling for tactical insights
      if (modalType === 'tactical-insights') {
        return insightsApi.getTacticalInsightsDeals(
          criteria!,
          currentPage,
          pageSize,
          cycleTypesString,
          selectedAccountValues && selectedAccountValues.length > 0 ? selectedAccountValues.join(',') : undefined,
          selectedSalesRep,
          selectedTeam
        );
      }

      // Special handling for open cycles stage
      if (stageName === 'Open sales cycles with no deals') {
        // NEW: Use the qualification category directly (TRANSACTIONAL_DEAL, TRANSACTIONAL_ZERO, ENTERPRISE, NURTURE, BAD_FIT, TRASH, or 'all')
        // The category prop is already set correctly from the click handler (Conflict removed)
        const categoryToUse = category || 'all';

        return salesCyclesApi.getOpenCyclesByCategory(
          categoryToUse,
          currentPage,
          pageSize,
          cycleTypesString,
          selectedAccountValues && selectedAccountValues.length > 0 ? selectedAccountValues.join(',') : undefined,
          selectedSalesRep,
          selectedTeam,
          null, // qualificationType - not used with new system
          null  // actionType - not used with new system
        );
      }

      if (modalType === 'attention') {
        return salesCyclesApi.getDealsByStage(stageName, {
          category: 'attention',
          page: currentPage,
          pageSize: pageSize,
          cycleType: cycleTypesString,
          accountValue: selectedAccountValues && selectedAccountValues.length > 0 ? selectedAccountValues.join(',') : undefined,
          salesRep: selectedSalesRep,
          team: selectedTeam
        });
      } else {
        return salesCyclesApi.getDealsByStage(stageName, {
          category: category!,
          page: currentPage,
          pageSize: pageSize,
          cycleType: cycleTypesString,
          accountValue: selectedAccountValues && selectedAccountValues.length > 0 ? selectedAccountValues.join(',') : undefined,
          salesRep: selectedSalesRep,
          team: selectedTeam
        });
      }
    },
    {
      enabled: isOpen,
      staleTime: 1 * 60 * 1000, // 1 minute for deal lists
      cacheTime: 3 * 60 * 1000, // 3 minutes cache
      refetchOnWindowFocus: false,
    }
  );


  const getScoreColor = (score: number) => {
    if (modalType === 'attention') {
      // Attention modals focus on lower performing deals
      if (score < 2.0) return 'text-red-500';
      if (score < 3.0) return 'text-orange-500';
      return 'text-orange-500';
    } else {
      // Category modals show all performance levels
      if (score >= 4.0) return 'text-green-600';
      if (score >= 3.0) return 'text-orange-500';
      return 'text-orange-600';
    }
  };

  const getScoreBgColor = (score: number) => {
    if (modalType === 'attention') {
      if (score < 2.0) return 'bg-red-100 dark:bg-red-900/30';
      if (score < 3.0) return 'bg-orange-100 dark:bg-orange-900/30';
      return 'bg-orange-100 dark:bg-orange-900/30';
    } else {
      if (score >= 4.0) return 'bg-green-100 dark:bg-green-900/30';
      if (score >= 3.0) return 'bg-orange-100 dark:bg-orange-900/30';
      return 'bg-orange-200 dark:bg-orange-900/30';
    }
  };

  const getQualificationBadge = (qualification: string | null, hasAnalysisRecord?: boolean, hasMeetingContent?: boolean) => {
    // If no qualification value - distinguish between different states
    if (!qualification) {
      // First check if there's no meeting content (no event_content data)
      if (hasMeetingContent === false) {
        // No meeting content - cycle has no analyzable meeting data
        return <span className="text-xs text-gray-400 italic font-inter">No meetings/emails recorded</span>;
      }
      // Check if analysis record exists
      if (hasAnalysisRecord === false) {
        // No analysis record - cycle hasn't been analyzed yet
        return <span className="text-xs text-gray-400 italic font-inter">Not analyzed</span>;
      } else if (hasAnalysisRecord === true) {
        // Has analysis record but NULL qualification - analysis failed or incomplete
        return <span className="text-xs text-gray-400 italic font-inter">No qualification</span>;
      } else {
        // Unknown state (shouldn't happen but handle gracefully)
        return <span className="text-xs text-gray-400 italic font-inter">Not analyzed</span>;
      }
    }

    const badges: Record<string, { color: string; label: string }> = {
      'TRANSACTIONAL': { color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300', label: 'Open transactional' },
      'ENTERPRISE': { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300', label: 'Open Enterprise stage zero' },
      'NURTURE': { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', label: 'Nurture' },
      'BAD_FIT': { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', label: 'Bad Fit' },
      'TRASH': { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', label: 'Trash' }
    };

    // Check if it's a predefined badge
    const badge = badges[qualification];
    if (badge) {
      return (
        <span className={`px-2 py-1 rounded text-xs font-medium font-inter inline-flex ${badge.color}`}>
          {badge.label}
        </span>
      );
    }

    // For any other qualification values, show them as-is
    return <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded text-xs font-medium font-inter">{qualification}</span>;
  };

  // Transform data based on modal type
  const transformedData = React.useMemo(() => {
    if (!deals) return { dealsList: [], pagination: null };

    if (modalType === 'strategic-insights') {
      // Strategic insights API returns { deals, pagination }
      return {
        dealsList: (deals as any).deals || [],
        pagination: (deals as any).pagination || null
      };
    } else if (modalType === 'tactical-insights') {
      // Tactical insights API returns { criteria_name, criteria_display_name, deals, pagination }
      return {
        dealsList: (deals as any).deals || [],
        pagination: (deals as any).pagination || null
      };
    } else if (stageName === 'Open sales cycles with no deals') {
      // Open cycles API returns { data, pagination }
      return {
        dealsList: (deals as any).data || [],
        pagination: (deals as any).pagination || null
      };
    } else {
      // Regular stage deals API now returns { deals, pagination }
      return {
        dealsList: (deals as any).deals || [],
        pagination: (deals as any).pagination || null
      };
    }
  }, [deals, modalType]);

  const { dealsList, pagination } = transformedData;

  // Stage criteria mapping
  const getStageCriteria = (stageName: string) => {
    switch (stageName) {
      case 'Discovery':
        return [
          { key: 'identified_pain_points', label: 'Pain Points Identified' },
          { key: 'identified_champion', label: 'Champion Identified' },
          { key: 'identified_economic_buyer', label: 'Economic Buyer Identified' }
        ];
      case 'Business Case':
        return [
          { key: 'business_case_meeting_with_champion', label: 'Business Case Meeting with Champion' },
          { key: 'roi_calculated_by_rep', label: 'ROI Calculated by Rep' },
          { key: 'understand_paper_process', label: 'Understand Paper Process' }
        ];
      case 'Validation':
        return [
          { key: 'champion_confirmed_smartcat_can_solve_pains', label: 'Champion Confirmation' },
          { key: 'date_set_for_eb_meeting', label: 'Economic Buyer Meeting Scheduled' }
        ];
      case 'Economic Buyer Meeting':
        return [
          { key: 'eb_accepted_business_case', label: 'Economic Buyer Accepted Business Case' }
        ];
      case 'Legal & Procurement':
        return [
          { key: 'paperwork_under_review', label: 'Paperwork Under Review' },
          { key: 'estimated_signature_date', label: 'Estimated Signature Date' }
        ];
      default:
        return [];
    }
  };

  // Check if a criterion is completed for a deal
  const isCriterionCompleted = (deal: any, criterionKey: string) => {
    // Use the actual API data to determine completion
    // The API returns individual criteria values where > 0 means completed
    const criterionValue = deal[criterionKey];
    return criterionValue !== null && criterionValue !== undefined && criterionValue > 0;
  };

  const getModalTitle = () => {
    console.log('🔍 Modal Title Debug:', {
      modalType,
      stageName,
      categoryDisplayName,
      qualificationType,
      actionType
    });
    
    if (modalType === 'attention') {
      return `${icon} ${stageName} - Deals Needing Attention`;
    } else if (modalType === 'tactical-insights' || modalType === 'strategic-insights') {
      return `${icon} ${stageName}`;
    } else {
      return `${icon} ${stageName} - ${categoryDisplayName}`;
    }
  };

  const getEmptyStateMessage = () => {
    if (modalType === 'attention') {
      return `No deals found needing attention in ${stageName} stage`;
    } else if (modalType === 'tactical-insights') {
      return `No deals found for ${stageName}`;
    } else if (modalType === 'strategic-insights') {
      return `No deals found for ${stageName}`;
    } else {
      return `No deals found for ${categoryDisplayName?.toLowerCase()} in ${stageName} stage`;
    }
  };

  const getEmptyStateSubtext = () => {
    if (modalType === 'attention') {
      return 'All deals in this stage have adequate scoring progress.';
    } else if (modalType === 'tactical-insights') {
      return 'No deals match this exit criteria.';
    } else if (modalType === 'strategic-insights') {
      return 'No deals match this loss reason.';
    } else {
      return 'This category currently has no matching deals.';
    }
  };

  const modalTitle = getModalTitle();
  const showBackButton = stageName === 'Open sales cycles with no deals' && onBackToSelection;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      maxWidth="max-w-7xl"
    >
      {/* Back arrow button in upper left corner */}
      {showBackButton && (
        <div className="absolute top-3 left-3 z-10">
          <button
            onClick={onBackToSelection}
            className="flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm"
            aria-label="Back to selection"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        </div>
      )}
      {/* Error state - show at top level */}
      {error ? (
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <div className="text-red-600 dark:text-red-400 font-medium font-inter">Error loading deals</div>
          </div>
          <div className="text-red-500 dark:text-red-400 text-sm mt-1 font-inter">
            Unable to fetch deals data. Please try again later.
          </div>
        </div>
      ) : null}

      {/* Deals List Section - Fixed height to prevent modal size changes */}
      <div className="overflow-auto" style={{ height: '700px', maxHeight: 'calc(100vh - 250px)' }}>
        {isLoading ? (
          // Loading state inside the deals list area - maintain original modal size
          <div className="flex flex-col items-center justify-center h-full space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <div className="text-gray-600 dark:text-gray-400 text-sm font-inter">
              Loading {modalType === 'attention' ? 'deals needing attention' : categoryDisplayName?.toLowerCase()}...
            </div>
          </div>
        ) : !dealsList || dealsList.length === 0 ? (
          // Empty state inside the deals list area
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
            <div className="text-gray-900 dark:text-gray-100 text-lg font-inter">
              {getEmptyStateMessage()}
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-sm mt-2 font-inter">
              {getEmptyStateSubtext()}
            </div>
          </div>
        ) : stageName === 'Open sales cycles with no deals' ? (
          // Table layout for Open sales cycles with no deals - Dashboard view
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-inter">
                  Company
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-inter">
                  Qualification
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-inter">
                  Reason
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-inter">
                  Owner
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-inter">
                  Team
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {dealsList.map((deal: any, index: number) => (
                <tr
                  key={deal.sales_cycle_id || `cycle-${index}`}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {/* Company Name */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div
                      className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer hover:text-purple-600 dark:hover:text-purple-400 transition-colors flex items-center space-x-1 font-inter"
                      onClick={() => handleCompanyNameClick(undefined, deal.sales_cycle_id, deal.company_name)}
                      title="Open Sales Cycle in HubSpot (opens in new tab)"
                    >
                      <span>{deal.company_name || 'Unknown Company'}</span>
                      {deal.sales_cycle_id && (
                        <ExternalLink className="h-3 w-3 text-purple-600 hover:text-purple-700 flex-shrink-0" />
                      )}
                    </div>
                  </td>

                  {/* Qualification */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    {getQualificationBadge(deal.qualification_status, deal.has_analysis_record, deal.has_meeting_content)}
                  </td>

                  {/* Qualification Reason */}
                  <td className="px-4 py-3 max-w-2xl">
                    <div className="text-xs text-gray-600 dark:text-gray-400 font-inter leading-relaxed line-clamp-3">
                      {deal.qualification_reason || 'No reason provided'}
                    </div>
                  </td>

                  {/* Owner */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center space-x-1">
                      <User className="h-3 w-3 text-gray-400" />
                      <span className="text-sm text-gray-900 dark:text-gray-100 font-inter">{deal.cycle_owner_name || 'Unassigned'}</span>
                    </div>
                  </td>

                  {/* Team */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-gray-100 font-inter">{deal.cycle_owner_team || '-'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : modalType === 'strategic-insights' || modalType === 'tactical-insights' ? (
          // Card layout for strategic/tactical insights
          <div className="space-y-3">
          {dealsList.map((deal: any, index: number) => (
             <div
               key={deal.deal_id || deal.sales_cycle_id || `deal-${index}`}
               className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
             >
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <h3
                    className="text-lg font-bold truncate text-gray-900 dark:text-gray-100 cursor-pointer hover:text-purple-600 dark:hover:text-purple-400 transition-colors flex items-center space-x-2 font-inter"
                    onClick={() => handleCompanyNameClick(undefined, deal.sales_cycle_id, deal.company_name)}
                    title="Open Sales Cycle in HubSpot (opens in new tab)"
                  >
                    <span>{deal.company_name || 'Unknown Company'}</span>
                    {deal.sales_cycle_id && (
                      <ExternalLink className="h-4 w-4 text-purple-600 hover:text-purple-700" />
                    )}
                  </h3>
                </div>

                {/* Sales Cycle Info */}
                {modalType === 'strategic-insights' ? (
                  <div className="mt-2 mb-1">
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border-l-4 border-gray-300 dark:border-gray-500 rounded-r text-xs text-gray-600 dark:text-gray-400 font-inter leading-relaxed">
                      <div className="font-medium text-gray-800 dark:text-gray-200 mb-1">Primary Loss Reason:</div>
                      <div>{deal.why_closed_lost_primary || deal.how_closed_lost || 'No details available'}</div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 mb-1">
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border-l-4 border-gray-300 dark:border-gray-500 rounded-r text-xs text-gray-600 dark:text-gray-400 font-inter leading-relaxed">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-800 dark:text-gray-200">Deal Status:</span>
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          deal.status === 'CLOSED_WON'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        }`}>
                          {deal.status === 'CLOSED_WON' ? 'Won' : 'Lost'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Owner and Team */}
                <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mt-2 font-inter">
                  <div className="flex items-center space-x-1">
                    <User className="h-4 w-4" />
                    <span>{deal.cycle_owner_name || 'Unassigned'}</span>
                  </div>
                  {deal.cycle_owner_team && (
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs font-inter">
                      {deal.cycle_owner_team}
                    </span>
                  )}
                  {(deal.deal_amount || deal.total_deal_amount) && (deal.deal_amount > 0 || deal.total_deal_amount > 0) && (
                    <span className="text-green-600 text-sm font-bold font-inter">
                      ${(deal.deal_amount || deal.total_deal_amount).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
          </div>
        ) : (
          // Table layout for regular stage deals
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-inter">
                  Score
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-inter">
                  Company
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-inter">
                  {stageName} Criteria
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-inter">
                  Owner
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-inter">
                  Team
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-inter">
                  Account Value
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-inter">
                  Type
                </th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-inter">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {dealsList.map((deal: any, index: number) => (
                <tr
                  key={deal.deal_id || `deal-${index}`}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {/* Score */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    {deal.has_scoring_data === 1 || (deal.has_scoring_data === undefined && deal.current_stage_score !== null && deal.current_stage_score !== undefined && deal.current_stage_score > 0) ? (
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreBgColor(deal.current_stage_score)} ${getScoreColor(deal.current_stage_score)} font-inter`}>
                        {deal.current_stage_score.toFixed(1)}
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 font-inter">
                        No Score
                      </span>
                    )}
                  </td>

                  {/* Company Name */}
                  <td className="px-4 py-3" style={{ maxWidth: '200px' }}>
                    <div
                      className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer hover:text-purple-600 dark:hover:text-purple-400 transition-colors flex items-start space-x-1 font-inter"
                      onClick={() => handleCompanyNameClick(deal.deal_id, undefined, deal.company_name)}
                      title="Open Deal in HubSpot (opens in new tab)"
                    >
                      <span className="break-words">{deal.company_name || 'Unknown Company'}</span>
                      {deal.deal_id && (
                        <ExternalLink className="h-3 w-3 text-purple-600 hover:text-purple-700 flex-shrink-0 mt-0.5" />
                      )}
                    </div>
                  </td>

                  {/* Discovery Criteria with Labels */}
                  <td className="px-4 py-3">
                    {deal.has_scoring_data === 1 || (deal.has_scoring_data === undefined && deal.current_stage_score !== null && deal.current_stage_score !== undefined && deal.current_stage_score > 0) ? (
                      <div className="space-y-1">
                        {getStageCriteria(stageName).map((criterion: { key: string; label: string; }) => {
                          const isCompleted = isCriterionCompleted(deal, criterion.key);
                          return (
                            <div key={criterion.key} className="flex items-center space-x-2">
                              {isCompleted ? (
                                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                              )}
                              <span className={`text-xs font-inter ${isCompleted ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
                                {criterion.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 font-inter">No scoring data</span>
                    )}
                  </td>

                  {/* Owner */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center space-x-1">
                      <User className="h-3 w-3 text-gray-400" />
                      <span className="text-sm text-gray-900 dark:text-gray-100 font-inter">{deal.cycle_owner_name || 'Unassigned'}</span>
                    </div>
                  </td>

                  {/* Team */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-gray-100 font-inter">{deal.cycle_owner_team || '-'}</span>
                  </td>

                  {/* Account Value */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-gray-100 font-inter">{deal.account_tier || '-'}</span>
                  </td>

                  {/* Cycle Type */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-gray-100 font-inter">
                      {deal.cycle_type === 'NEWBUSINESS' ? 'New Business' : deal.cycle_type === 'EXPANSION' ? 'Expansion' : deal.cycle_type || '-'}
                    </span>
                  </td>

                  {/* Deal Amount */}
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    {deal.deal_amount && deal.deal_amount > 0 ? (
                      <span className="text-sm font-bold text-green-600 font-inter">
                        ${deal.deal_amount.toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 font-inter">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Controls - Always show at bottom */}
      <div className="flex items-center justify-between mt-4 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="flex items-center space-x-4">
          <span className="text-gray-700 dark:text-gray-300 text-sm font-inter">
            {isLoading ? (
              'Loading...'
            ) : dealsList && dealsList.length > 0 ? (
              <>Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, pagination ? pagination.total_deals : dealsList.length)} of {pagination ? pagination.total_deals : dealsList.length} deals</>
            ) : (
              'No deals to display'
            )}
          </span>
          <div className="flex items-center space-x-2">
            <label className="text-gray-700 dark:text-gray-300 text-sm font-inter">Items per page:</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1); // Reset to first page when changing page size
              }}
              className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 font-inter"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1 || isLoading}
            className="flex items-center space-x-1 px-3 py-1 rounded bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-inter"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="text-sm">Previous</span>
          </button>

          <span className="text-gray-700 dark:text-gray-300 text-sm px-3 font-inter">
            Page {currentPage} of {pagination ? pagination.total_pages : Math.ceil((dealsList?.length || 1) / pageSize)}
          </span>

          <button
            onClick={() => setCurrentPage(prev => prev + 1)}
            disabled={currentPage >= (pagination ? pagination.total_pages : Math.ceil((dealsList?.length || 1) / pageSize)) || isLoading}
            className="flex items-center space-x-1 px-3 py-1 rounded bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-inter"
          >
            <span className="text-sm">Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DealsListModal;